'use client';

import { useState, useCallback, useEffect } from 'react';
import type React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Divider, Alert } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ChainType } from '@/constants/chain-specific';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import TokenSwapInput from '@/components/Input/SwapInput';
import WalletInput from '@/components/Input/WalletInput';
import { formatNumber } from '@/util/balance/formatNumber';
import { debounce } from 'lodash';
import { resolve } from '@/lib/dns-names';
import { isAddress } from 'viem';
import type { Address } from 'viem';
import type { TokenDataAllType } from '@/constants/types/currency';
import { supportedChainIds, type RequiredAggregatorChainType } from '@/constants/project-specific';
import { useLPTokenBalance } from '@/hooks/liquidity/useLPTokenBalance';
import { useSendLPToken } from '@/hooks/liquidity/useSendLPToken';
import { useConnection } from 'wagmi';
interface SendLPModalProps {
  position: LiquidityPosition;
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: (isOpen: boolean) => void;
  chainId?: ChainType;
  tokenData?: Record<Address, TokenDataAllType>;
}
export function SendLPModal({
  position,
  isOpen,
  onClose,
  onOpenChange,
  chainId,
  tokenData
}: SendLPModalProps) {
  const {
    address: userAddress
  } = useConnection();
  const [amount, setAmount] = useState<string>('');
  const [sendAddress, setSendAddress] = useState<string>('');
  const [resolvedAddress, setResolvedAddress] = useState<string>('');
  const [isResolving, setIsResolving] = useState(false);

  // Get LP token balance
  const {
    balance: lpBalance,
    formattedBalance: lpFormattedBalance,
    decimals: lpDecimals,
    isLoading: isBalanceLoading,
    error: balanceError
  } = useLPTokenBalance({
    lpTokenAddress: position.contractAddress as Address,
    userAddress: userAddress,
    chainId,
    enabled: isOpen && !!userAddress
  });

  // Send LP token hook
  const {
    sendLPTokens,
    isLoading: isSending,
    isSuccess,
    error: sendError
  } = useSendLPToken({
    lpTokenAddress: position.contractAddress as Address,
    chainId,
    onSuccess: () => {
      setAmount('');
      setSendAddress('');
      setResolvedAddress('');
      onClose();
    },
    onError: error => {
      console.error('Failed to send LP tokens:', error);
    }
  });

  // Create a virtual token config for the LP token
  const lpToken = {
    address: position.contractAddress,
    symbol: `${position.token0.symbol}/${position.token1.symbol}`,
    name: `${position.token0.name}-${position.token1.name} LP`,
    decimals: lpDecimals,
    logo: position.token0.logo // Use first token's logo as fallback
  };
  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
  }, []);

  // Debounced address resolution
  const debouncedResolve = useCallback(debounce(async (address: string) => {
    if (!address) {
      setResolvedAddress('');
      return;
    }
    setIsResolving(true);
    try {
      if (isAddress(address)) {
        setResolvedAddress(address);
      } else {
        const resolved = await resolve(address, chainId ? Number(chainId) : undefined);
        setResolvedAddress(resolved || '');
      }
    } catch (error_0) {
      console.error('Error resolving address:', error_0);
      setResolvedAddress('');
    } finally {
      setIsResolving(false);
    }
  }, 500), [chainId]);
  const handleSendAddressChange = useCallback(async (wallet: string | null) => {
    if (!wallet) {
      setSendAddress('');
      setResolvedAddress('');
      return;
    }
    setSendAddress(wallet);
    if (wallet.startsWith("0x")) {
      setResolvedAddress(wallet);
    } else {
      debouncedResolve(wallet);
    }
  }, [debouncedResolve]);
  const handleSend = async () => {
    const targetAddress = resolvedAddress || sendAddress;

    // Validate amount against balance
    if (parseFloat(amount) > parseFloat(lpFormattedBalance)) {
      return; // Error will be shown in UI
    }
    await sendLPTokens({
      to: targetAddress,
      amount,
      decimals: lpDecimals
    });
  };
  const isValidAddress = resolvedAddress || isAddress(sendAddress);
  const hasInsufficientBalance = amount && parseFloat(amount) > parseFloat(lpFormattedBalance);
  const canSend = amount && parseFloat(amount) > 0 && isValidAddress && !hasInsufficientBalance && !isSending;
  return <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg" backdrop="blur">
      <ModalContent>
        <ModalHeader className="flex items-center gap-3">
          <Icon icon="mdi:send" className="text-xl" />
          <div>
            <h3 className="text-lg font-semibold">Send LP Tokens</h3>
            <p className="text-sm text-default-500">
              {position.token0.symbol}/{position.token1.symbol} Position
            </p>
          </div>
        </ModalHeader>
        
        <ModalBody className="space-y-4">
          {/* Position Info */}
          <div className="p-4 bg-default-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-default-500">Position Value</span>
              <span className="font-medium">${formatNumber(position.totalValueUsd, 2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-default-500">Your Share</span>
              <span className="text-sm">
                {formatNumber(parseFloat(position.token0AmountFormatted), 4)} {position.token0.symbol} + {' '}
                {formatNumber(parseFloat(position.token1AmountFormatted), 4)} {position.token1.symbol}
              </span>
            </div>
          </div>

          <Divider />

          {/* Amount Input */}
          <TokenSwapInput chainId={chainId as RequiredAggregatorChainType} onSelectClick={() => {}} // LP token is fixed, no selection
        balance={parseFloat(lpFormattedBalance)} label="Amount to Send" selectedToken={lpToken} onChange={handleAmountChange} value={amount} readOnly={false} />

          {/* Balance and Error Display */}
          <div className="flex justify-between items-center text-sm">
            <span className="text-default-500">
              Available: {isBalanceLoading ? 'Loading...' : `${formatNumber(parseFloat(lpFormattedBalance), 6)} LP`}
            </span>
            {hasInsufficientBalance && <span className="text-danger">Insufficient balance</span>}
          </div>

          {/* Balance Error */}
          {balanceError && <Alert title="Failed to load LP token balance. Please try again." />}

          {/* Send Error */}
          {sendError && <Alert title={sendError.message} color="danger" />}

          {/* Recipient Address */}
          <WalletInput onChange={handleSendAddressChange} type="To Address" chainId={chainId ?? 109} />

          {/* Address Resolution Status */}
          {sendAddress && <div className="text-sm">
              {isResolving ? <div className="flex items-center gap-2 text-default-500">
                  <Icon icon="mdi:loading" className="animate-spin" />
                  Resolving address...
                </div> : resolvedAddress ? <div className="flex items-center gap-2 text-success">
                  <Icon icon="mdi:check-circle" />
                  Resolved to: {resolvedAddress.slice(0, 6)}...{resolvedAddress.slice(-4)}
                </div> : !isAddress(sendAddress) ? <div className="flex items-center gap-2 text-danger">
                  <Icon icon="mdi:alert-circle" />
                  Invalid address or domain
                </div> : null}
            </div>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={isSending}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleSend} isDisabled={!canSend} isLoading={isSending} startContent={!isSending ? <Icon icon="mdi:send" /> : undefined}>
            {isSending ? 'Sending...' : 'Send LP Tokens'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>;
}